import { apiClient } from './client';
import type { AiModelAlias } from '@workchat/shared-types';

/**
 * AI 에이전트 — 목록 · 만들기 · 고치기 · 중지 (설계 판 E).
 *
 * ★★★ **「고칠 수 있나」를 화면이 판정하지 않는다.** 서버가 `canEdit` 을 실어 준다 —
 *    역할 규칙을 양쪽에서 쓰면 언젠가 어긋나고, 그때 «보이는데 안 눌리는 단추»가 생긴다.
 * ★ **삭제가 없다.** 없애기는 중지(`status: 'STOPPED'`)다 — 지난 대화가 「어느 에이전트가
 *   답했나」를 가리키려면 그 에이전트가 남아 있어야 한다.
 */

export type AiAgentScope = 'PERSONAL' | 'TENANT';
export type AiAgentStatus = 'ACTIVE' | 'STOPPED';

export interface AiAgent {
  id: string;
  scope: AiAgentScope;
  name: string;
  description: string;
  /** 역할 — 「너는 무엇을 하는 사람인가」. */
  roleInstructions: string;
  /** 답변 규칙 — 「어떻게 답하는가」. */
  answerRules: string;
  /** 별칭. null = 안 정함 → 배포의 전역 선택이 답한다. */
  modelAlias: AiModelAlias | null;
  starters: string[];
  /**
   * 뒤질 도메인 — **빈 배열 = 전부**(판 E · 정의서 A5).
   * ★ 권한이 아니다. 도구는 늘 부른 사람 명의로 돌고, 좁혀도 그 사람이 볼 수 있는 것은 그대로다.
   */
  toolDomains: string[];
  status: AiAgentStatus;
  isMine: boolean;
  canEdit: boolean;
  updatedAt: string;
}

/** 화면의 거르개 — 「전체 · 전사 · 내가 만든」(정의서 A3). */
export type AiAgentFilter = 'all' | 'tenant' | 'mine';

export interface AiAgentDraft {
  name: string;
  description?: string;
  roleInstructions?: string;
  answerRules?: string;
  modelAlias?: AiModelAlias | null;
  starters?: string[];
  toolDomains?: string[];
  scope?: AiAgentScope;
}

export async function listAiAgents(filter: AiAgentFilter = 'all'): Promise<AiAgent[]> {
  const res = await apiClient.get<{ items: AiAgent[] }>('/assist/agents', { params: { filter } });
  return res.data.items ?? [];
}

export async function getAiAgent(id: string): Promise<AiAgent> {
  const res = await apiClient.get<AiAgent>(`/assist/agents/${id}`);
  return res.data;
}

export async function createAiAgent(draft: AiAgentDraft): Promise<AiAgent> {
  const res = await apiClient.post<AiAgent>('/assist/agents', draft);
  return res.data;
}

export async function updateAiAgent(
  id: string,
  patch: Partial<AiAgentDraft> & { status?: AiAgentStatus },
): Promise<AiAgent> {
  const res = await apiClient.patch<AiAgent>(`/assist/agents/${id}`, patch);
  return res.data;
}
